import { useState, useEffect, useRef } from "react";
import { FaNetworkWired, FaDesktop, FaLaptop, FaServer, FaVideo, FaTools, FaCog, FaCamera } from "react-icons/fa";
import { FaPlay } from "react-icons/fa";

// Images for the hero section slider
const heroImages = [
  {
    src: "/img/img1.jpeg",
    alt: "Sample Computer Repair",
  },
  {
    src: "/img/img2.jpeg",
    alt: "Laptop/Server Service",
  },
  {
    src: "/img/img3.jpeg",
    alt: "CCTV/Projector Install",
  },
  {
    src: "/img/img4.jpeg",
    alt: "IT Service",
  },
  {
    src: "/img/img5.jpeg",
    alt: "IT Professional",
  },
  {
    src: "/img/img6.jpeg",
    alt: "Laptop Fixing",
  },
  {
    src: "/img/img7.jpeg",
    alt: "Sample Computer Repair",
  },
  {
    src: "/img/img8.jpg",
    alt: "Laptop Fixing",
  },
  {
    src: "/img/img9.jpg",
    alt: "Sample Computer Repair",
  },
];

// Intersection Observer hook
function useOnScreen(ref, rootMargin = "0px") {
  // Returns true if element is on screen
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [fade, setFade] = useState(true);
  const timeoutRef = useRef(null);
  const sectionRef = useRef(null);
  const isVisible = useOnScreen(sectionRef, "-80px"); // Adjust if needed

  // Start animating only when component is visible
  useEffect(() => {
    if (!isVisible) {
      // Reset to first image and visible, fade in, pause slider
      setCurrent(0);
      setFade(true);
      clearTimeout(timeoutRef.current);
      return;
    }
    setFade(true);
    timeoutRef.current = setTimeout(() => {
      setFade(false);
      timeoutRef.current = setTimeout(() => {
        setCurrent((c) => (c + 1) % heroImages.length);
        setFade(true);
      }, 350); // fade out duration (ms)
    }, 2000); // show image for 2 seconds

    return () => clearTimeout(timeoutRef.current);
  }, [current, isVisible]);

  // If user navigates away, clear timer
  useEffect(() => {
    if (!isVisible) {
      clearTimeout(timeoutRef.current);
    }
    return () => clearTimeout(timeoutRef.current);
    // eslint-disable-next-line
  }, [isVisible]);

  // Handle manual dot click to change image immediately
  const handleDotClick = (idx) => {
    setCurrent(idx);
    setFade(true);
    clearTimeout(timeoutRef.current);
  };

  return (
    <section
      ref={sectionRef}
      className="bg-gray-100 pb-16 pt-40 font-PTSerif-Regular"
    >
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-8 items-center">

        {/* Left Content */}
        <div className="flex flex-col justify-center h-full">
          <h2 className={`text-3xl lg:text-4xl font-bold text-gray-900 leading-snug font-PTSerif-Regular transition-all duration-700
            ${isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}
          `}>
            We Offer <span className="text-blue-500">Expert AMC & IT Services</span>
          </h2>

          <h1 className={`text-xl lg:text-2xl font-bold text-blue-500 italic mt-3 font-PTSerif-Regular transition-all duration-700 delay-100
            ${isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}
          `}>
            NETWORKING | COMPUTER | LAPTOP | SERVER | PROJECTOR | CCTV
          </h1>

          <p className={`text-lg lg:text-xl font-semibold text-gray-900 mt-4 font-PTSerif-Regular transition-all duration-700 delay-200
            ${isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}
          `}>
            AMC, Installation & All IT Solutions Since 2014
          </p>

          {/* Buttons */}
          <div className={`flex gap-4 mt-6 flex-wrap transition-all duration-700 delay-300
            ${isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}
          `}>
            <a
              href="/contact"
              className="bg-gray-700 text-white px-5 py-2.5 rounded-md font-semibold hover:bg-gray-800 transition text-sm font-PTSerif-Regular"
            >
              BOOK APPOINTMENT
            </a>
          </div>

          {/* Services Grid */}
          <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 font-PTSerif-Regular transition-all duration-700 delay-400
            ${isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}
          `}>
            <div className="bg-white py-4 px-2 rounded-md shadow text-center flex flex-col items-center font-PTSerif-Regular">
              <FaNetworkWired className="text-blue-500 text-xl mb-2" />
              <p className="text-gray-700 font-medium text-sm font-PTSerif-Regular">NETWORKING AMC</p>
            </div>
            <div className="bg-white py-4 px-2 rounded-md shadow text-center flex flex-col items-center font-PTSerif-Regular">
              <FaDesktop className="text-blue-500 text-xl mb-2" />
              <p className="text-gray-700 font-medium text-sm font-PTSerif-Regular">COMPUTER AMC</p>
            </div>
            <div className="bg-white py-4 px-2 rounded-md shadow text-center flex flex-col items-center font-PTSerif-Regular">
              <FaLaptop className="text-blue-500 text-xl mb-2" />
              <p className="text-gray-700 font-medium text-sm font-PTSerif-Regular">LAPTOP AMC</p>
            </div>
            <div className="bg-white py-4 px-2 rounded-md shadow text-center flex flex-col items-center font-PTSerif-Regular">
              <FaServer className="text-blue-500 text-xl mb-2" />
              <p className="text-gray-700 font-medium text-sm font-PTSerif-Regular">SERVER AMC</p>
            </div>
            <div className="bg-white py-4 px-2 rounded-md shadow text-center flex flex-col items-center font-PTSerif-Regular">
              <FaVideo className="text-blue-500 text-xl mb-2" />
              <p className="text-gray-700 font-medium text-sm font-PTSerif-Regular">PROJECTOR AMC</p>
            </div>
            <div className="bg-white py-4 px-2 rounded-md shadow text-center flex flex-col items-center font-PTSerif-Regular">
              <FaCamera className="text-blue-500 text-xl mb-2" />
              <p className="text-gray-700 font-medium text-sm font-PTSerif-Regular">CCTV AMC</p>
            </div>
            <div className="bg-white py-4 px-2 rounded-md shadow text-center flex flex-col items-center font-PTSerif-Regular">
              <FaTools className="text-blue-500 text-xl mb-2" />
              <p className="text-gray-700 font-medium text-sm font-PTSerif-Regular">CCTV INSTALLATION</p>
            </div>
            <div className="bg-white py-4 px-2 rounded-md shadow text-center flex flex-col items-center font-PTSerif-Regular">
              <FaCog className="text-blue-500 text-xl mb-2" />
              <p className="text-gray-700 font-medium text-sm font-PTSerif-Regular">SERVICE & ALL IT SERVICES</p>
            </div>
          </div>
        </div>

        {/* Right Image with fade animation */}
        <div className="relative flex justify-center">
          <div className={`bg-white p-4 shadow-lg font-PTSerif-Regular transition-all duration-700 delay-500
            ${isVisible ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"}
          `}>
            <img
              key={heroImages[current].src}
              src={heroImages[current].src}
              alt={heroImages[current].alt}
              className={`
                w-[350px] h-[350px] md:w-[380px] md:h-[380px] lg:w-[400px] lg:h-[400px] object-cover
                transition-opacity duration-350
                ${fade ? "opacity-100" : "opacity-0"}
                font-PTSerif-Regular
              `}
              style={{
                transitionProperty: 'opacity'
              }}
            />

            {/* Slider dots */}
            <div className="flex justify-center gap-2 mt-4 font-PTSerif-Regular">
              {heroImages.map((img, idx) => (
                <span
                  key={img.src}
                  onClick={() => handleDotClick(idx)}
                  className={`w-3 h-3 rounded-full cursor-pointer transition
                    ${current === idx ? 'bg-blue-500' : 'border border-gray-400 bg-white'}
                  `}
                  title={img.alt}
                ></span>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}